import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from '@/components/ui/toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import '@/styles/globals.css';

// Layouts
import AuthCallbackPage from '@/pages/auth/callback';
import { AppLayout } from '@/layouts/AppLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

// Auth guards
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireAdminAuth } from '@/components/auth/RequireAdminAuth';
import { RequireRole } from '@/components/auth/RequireRole';

// Lazy-loaded pages
const SignInPage = lazy(() => import('@/pages/sign-in'));
const SignUpPage = lazy(() => import('@/pages/sign-up'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const ServicesPage = lazy(() => import('@/pages/services'));
const ServiceNewPage = lazy(() => import('@/pages/services/new'));
const ServiceDetailPage = lazy(() => import('@/pages/services/[id]'));
const ServiceReadinessPage = lazy(() => import('@/pages/services/[id]/readiness'));
const ServiceEditPage = lazy(() => import('@/pages/services/[id]/edit'));
const ServiceMusicPage = lazy(() => import('@/pages/services/[id]/music'));
const ReviewPage = lazy(() => import('@/pages/review'));
const ReviewDetailPage = lazy(() => import('@/pages/review/[id]'));
const SubmissionsNewPage = lazy(() => import('@/pages/submissions/new'));
const SubmissionsArchivePage = lazy(() => import('@/pages/submissions/archive'));
const ReportNewPage = lazy(() => import('@/pages/reports/new'));
const ReportTemplatesPage = lazy(() => import('@/pages/reports/templates'));
const ReportTemplatesOrphansPage = lazy(() => import('@/pages/reports/templates-orphans'));
const ReportRunsPage = lazy(() => import('@/pages/reports/runs'));
const ReportRunDetailPage = lazy(() => import('@/pages/reports/run-detail'));
const ReportInboxPage = lazy(() => import('@/pages/reports/inbox'));
const ReportFinancePage = lazy(() => import('@/pages/reports/finance'));
const AuxiliariesIndexPage = lazy(() => import('@/pages/auxiliaries/index'));
const AuxiliaryNewPage = lazy(() => import('@/pages/auxiliaries/new'));
const AuxiliaryDetailPage = lazy(() => import('@/pages/auxiliaries/[id]'));
const SubmissionDetailPage = lazy(() => import('@/pages/submissions/[id]'));
const SubmissionEditPage = lazy(() => import('@/pages/submissions/[id]/edit'));
const NotificationsPage = lazy(() => import('@/pages/notifications'));
const MediaPage = lazy(() => import('@/pages/media'));
const SettingsPage = lazy(() => import('@/pages/settings'));
const SettingsRoutingPage = lazy(() => import('@/pages/settings/routing'));
const SettingsMembersPage = lazy(() => import('@/pages/settings/members'));
const SettingsChurchPage = lazy(() => import('@/pages/settings/church'));
const SettingsIntegrationsPage = lazy(() => import('@/pages/settings/integrations'));
const TemplatesPersonalPage = lazy(() => import('@/pages/settings/templates-personal'));
const TemplatesStandardPage = lazy(() => import('@/pages/settings/templates-standard'));
const TemplatesOrphansPage = lazy(() => import('@/pages/settings/templates-orphans'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const AdminLoginPage = lazy(() => import('@/pages/admin/login'));
const AdminDashboardPage = lazy(() => import('@/pages/admin'));
const ChurchDetailPage = lazy(() => import('@/pages/admin/churches/[churchId]'));

// Worship workflow pages
const CalendarPage = lazy(() => import('@/pages/calendar'));
const SongsPage = lazy(() => import('@/pages/songs'));
const SongDetailPage = lazy(() => import('@/pages/songs/[id]'));
const SongNewPage = lazy(() => import('@/pages/songs/new'));
const MyAssignmentsPage = lazy(() => import('@/pages/my-assignments'));
const ManagePage = lazy(() => import('@/pages/manage'));
const ManageServicesPage = lazy(() => import('@/pages/manage/services'));
const ManageGroupsPage = lazy(() => import('@/pages/manage/groups'));
const ManageElementsPage = lazy(() => import('@/pages/manage/elements'));

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={<div className="min-h-screen bg-warm-bg" />}>
        <Routes>
          {/* Auth routes (public) */}
          <Route path="/sign-in" element={<SignInPage />} />
          {/* Cornerstone OAuth redirect target — must match the redirect_uri
              registered on the OAuth client. Public: the user is not yet
              authenticated when they land here. */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />

          {/* App routes (require auth) */}
          <Route element={<RequireAuth><AppLayout><Outlet /></AppLayout></RequireAuth>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/new" element={<ServiceNewPage />} />
            <Route path="/services/:id" element={<ServiceDetailPage />} />
            <Route path="/services/:id/readiness" element={<ServiceReadinessPage />} />
            <Route path="/services/:id/edit" element={<ServiceEditPage />} />
            <Route path="/services/:id/music" element={<ServiceMusicPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/review/:id" element={<ReviewDetailPage />} />
            <Route path="/submissions/new" element={<SubmissionsNewPage />} />
            <Route path="/submissions/archive" element={<SubmissionsArchivePage />} />
            <Route path="/reports/new" element={<ReportNewPage />} />
            <Route path="/reports/templates" element={<ReportTemplatesPage />} />
            <Route path="/reports/templates/orphans" element={<RequireRole roles={['pastor', 'admin']}><ReportTemplatesOrphansPage /></RequireRole>} />
            <Route path="/reports/runs" element={<ReportRunsPage />} />
            <Route path="/reports/runs/:id" element={<ReportRunDetailPage />} />
            <Route path="/reports/inbox" element={<ReportInboxPage />} />
            <Route path="/reports/finance" element={<RequireRole roles={['pastor', 'admin', 'finance_team']}><ReportFinancePage /></RequireRole>} />
            <Route path="/auxiliaries" element={<AuxiliariesIndexPage />} />
            <Route path="/auxiliaries/new" element={<RequireRole roles={['pastor', 'admin']}><AuxiliaryNewPage /></RequireRole>} />
            <Route path="/auxiliaries/:id" element={<AuxiliaryDetailPage />} />
            <Route path="/submissions/:id" element={<SubmissionDetailPage />} />
            <Route path="/submissions/:id/edit" element={<SubmissionEditPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/media" element={<MediaPage />} />
            <Route path="/songs" element={<SongsPage />} />
            <Route path="/songs/new" element={<SongNewPage />} />
            <Route path="/songs/:id" element={<SongDetailPage />} />
            <Route path="/my-assignments" element={<MyAssignmentsPage />} />
            <Route path="/manage" element={<RequireRole roles={['pastor', 'admin', 'minister_of_music']}><ManagePage /></RequireRole>} />
            <Route path="/manage/services" element={<RequireRole roles={['pastor', 'admin', 'minister_of_music']}><ManageServicesPage /></RequireRole>} />
            <Route path="/manage/groups" element={<RequireRole roles={['pastor', 'admin', 'minister_of_music']}><ManageGroupsPage /></RequireRole>} />
            <Route path="/manage/elements" element={<RequireRole roles={['pastor', 'admin', 'minister_of_music']}><ManageElementsPage /></RequireRole>} />
            <Route path="/settings" element={<RequireRole roles={['pastor', 'admin']}><SettingsPage /></RequireRole>} />
            <Route path="/settings/routing" element={<RequireRole roles={['pastor', 'admin']}><SettingsRoutingPage /></RequireRole>} />
            <Route path="/settings/members" element={<RequireRole roles={['pastor', 'admin']}><SettingsMembersPage /></RequireRole>} />
            <Route path="/settings/church" element={<RequireRole roles={['pastor', 'admin']}><SettingsChurchPage /></RequireRole>} />
            <Route path="/settings/integrations" element={<RequireRole roles={['pastor', 'admin']}><SettingsIntegrationsPage /></RequireRole>} />
            <Route path="/settings/templates/personal" element={<TemplatesPersonalPage />} />
            <Route path="/settings/templates/standard" element={<TemplatesStandardPage />} />
            <Route path="/settings/templates/orphans" element={<RequireRole roles={['pastor', 'admin']}><TemplatesOrphansPage /></RequireRole>} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route element={<RequireAdminAuth><AdminLayout><Outlet /></AdminLayout></RequireAdminAuth>}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/churches/:churchId" element={<ChurchDetailPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </BrowserRouter>
    </ErrorBoundary>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
